import { useCallback, useEffect, useState } from "react";
import { FileState, GetTrackResponse } from "../../proto/api_pb";
import { GetTrackVersionsResponse } from "../../proto/api_pb";
import { RpcError } from "grpc-web";
import { Box, Typography, LinearProgress } from "@mui/material";
import { fetchTrack } from "../../services/trackService";
import { fetchTrackVersions } from "../../services/trackService";
import { TrackVersions } from "../../components/TrackVersion/TrackVersion";
import { TrackVersionGroupProps } from "../../components/TrackVersion/types";
import { TrackObj, TrackVersionsObj } from "../../pages/MyTracks/types";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { styles } from "./styles";

export const TrackVersionGroup: React.FC<TrackVersionGroupProps> = ({
  id,
  selectedVersionId,
  handleChange,
  isPlaying,
  handleStop,
  evokeCallBack,
  allowDownload,
  shareToken,
}) => {
  const [trackVersions, setTrackVersions] = useState<TrackVersionsObj[]>([]);
  const [track, setTrack] = useState<TrackObj>();
  const [pageLoaded, setPageLoaded] = useState(false);
  const isSharedContentPage = window.location.pathname.includes("/share/");

  const { getValidAccessToken } = useGetValidAccessToken();
  const handlePlayPause = (id: string) => {
    handleStop(id);
    const currentSong = trackVersions.find(
      (item) => item.track?.getId() === id
    );
    if (currentSong) {
      if (currentSong.isPlaying) {
        const updatedTrackVersions = trackVersions.map((n) =>
          n.track?.getId() === id ? { ...n, isPlaying: false } : n
        );
        setTrackVersions(updatedTrackVersions);
      } else {
        const updatedTrack = trackVersions.map((n) => ({
          ...n,
          isPlaying: n.track?.getId() === id,
        }));
        setTrackVersions(updatedTrack);
        if (track) {
          track.waveSurfer?.stop();
          setTrack({ ...track, isPlaying: false });
        }
      }
    }
  };

  const stopAllVersionSongs = useCallback(() => {
    setTrackVersions((prev) =>
      prev.map((n) => ({
        ...n,
        isPlaying: false,
        selected: selectedVersionId === n.track?.getId(),
      }))
    );
  }, [selectedVersionId]);

  useEffect(() => {
    stopAllVersionSongs();
    // eslint-disable-next-line
  }, [evokeCallBack]);

  const handleFetch = useCallback(async () => {
    const token = isSharedContentPage
      ? shareToken ?? ""
      : await getValidAccessToken();
    try {
      const response = (await fetchTrack(token, id!)) as GetTrackResponse;
      setTrack({
        track: response.getTrack(),
        trackUrl: response.getTrackVersion()!.getTrackUrl(),
        isPlaying: false,
        waveSurfer: undefined,
        versionCount: undefined,
        latestVersion: undefined,
        notifications: undefined,
      });
    } catch (e) {
      const error = e as RpcError;
      console.log(error.code);
      console.log(error.message);
    }
  }, [getValidAccessToken, id, isSharedContentPage, shareToken]);

  const updateSelectedTrackVersion = () => {
    setTrackVersions(
      trackVersions.map((el) => ({
        ...el,
        isSelected: el.track?.getId() === selectedVersionId,
      }))
    );
  };

  const handleStopAll = useCallback(() => {
    if (isPlaying) stopAllVersionSongs();
  }, [isPlaying, stopAllVersionSongs]);

  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  useEffect(() => {
    handleStopAll();
  }, [handleStopAll]);

  const handleFetchTrackVersions = useCallback(async () => {
    const token = isSharedContentPage
      ? shareToken ?? ""
      : await getValidAccessToken();
    try {
      const response = (await fetchTrackVersions(
        token,
        id!
      )) as GetTrackVersionsResponse;
      const newTrackVersionList = response
        .getTrackVersionsList()
        .filter(
          (version) => version.getFileState() === FileState.FILE_STATE_ACTIVE
        )
        .map((el) => {
          return {
            track: el,
            isPlaying: false,
            isSelected: selectedVersionId === el.getId(),
          };
        });
      setTrackVersions(newTrackVersionList);
      setPageLoaded(true);
    } catch (e) {
      const error = e as RpcError;
      console.log(error.code);
      console.log(error.message);
    }
  }, [
    getValidAccessToken,
    id,
    isSharedContentPage,
    selectedVersionId,
    shareToken,
  ]);

  useEffect(() => {
    handleFetchTrackVersions();
  }, [handleFetchTrackVersions]);

  const handleOpenDeleteModal = (id: string) => {
    stopAllVersionSongs();
  };

  return (
    <>
      <Box>
        {pageLoaded && trackVersions.length > 0 && (
          <Typography sx={styles.emptyTypography} variant="h6"></Typography>
        )}
        <Box sx={styles.trackVersionsContainer}>
          {trackVersions &&
            trackVersions.map((el: any) => {
              const { isPlaying, isSelected } = el;
              return (
                <TrackVersions
                  key={el.track.getId()}
                  stopAllSongs={stopAllVersionSongs}
                  handlePlayPause={handlePlayPause}
                  isPlaying={isPlaying}
                  version={el.track}
                  isSelected={isSelected}
                  selectedVersionId={selectedVersionId}
                  handleChange={handleChange}
                  updateSelectedTrackVersion={updateSelectedTrackVersion}
                  handleOpenDeleteModal={handleOpenDeleteModal}
                  allowDownload={allowDownload}
                  shareToken={shareToken}
                />
              );
            })}
        </Box>
        {!pageLoaded && (
          <Box style={styles.progressContainer}>
            <Box sx={styles.linearProgressContainer}>
              <LinearProgress color="inherit" />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
